import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { useAllUsers, type AllUserRow } from '@/features/users/api';
import { useBulkAddLocationUsers, useLocationsList } from '../api';
import { useModuleFilter } from '@/lib/permissions/use-module-filter';
import { MODULE_LISTING_KEYS } from '@/lib/constants/modules';

export function BulkAddLocationPage() {
  const canBulkAdd = usePermission('Manage Locations.Bulk Location Add', BASE_PERMISSION.ADD);
  const locations = useLocationsList();
  const filteredLocations = useModuleFilter(locations.data ?? [], MODULE_LISTING_KEYS.MANAGE_LOCATIONS, (l) => l.locationId);
  const users = useAllUsers();
  const bulkAdd = useBulkAddLocationUsers();

  const [locationId, setLocationId] = useState<string>('');
  const [pending, setPending] = useState<{ ids: string[]; clear: () => void } | null>(null);

  const columns = useMemo<ColumnDef<AllUserRow>[]>(
    () => [
      {
        accessorKey: 'userId',
        header: 'User ID',
        size: 110,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.userId}
          </span>
        ),
      },
      { accessorKey: 'username', header: 'Username' },
    ],
    [],
  );

  const locationLabel =
    filteredLocations.find((l) => String(l.locationId) === locationId)?.locationName ??
    '';

  if (!canBulkAdd) return <Navigate to="/location-list" replace />;

  const performBulkAdd = () => {
    if (!pending) return;
    bulkAdd.mutate(
      { locationId, userIds: pending.ids.join(',') },
      {
        onSuccess: (res) => {
          toast.success(
            res?.message ?? `Added ${pending.ids.length} users to ${locationLabel}`,
          );
          pending.clear();
          setPending(null);
        },
        onError: (err) => toast.error(readErrorMessage(err) ?? 'Bulk add failed.'),
      },
    );
  };

  return (
    <FillPage>
      <PageHeader
        title="Bulk Add Users to Location"
        description="Attach many users to a location in one operation."
        actions={
          <Button asChild variant="outline">
            <Link to="/location-list">
              <ArrowLeft className="h-4 w-4" />
              Back to locations
            </Link>
          </Button>
        }
      />

      <Card>
        <CardHeader>
          <CardTitle>Target location</CardTitle>
          <CardDescription>
            Users you select below will be added to this location.
          </CardDescription>
        </CardHeader>
        <CardContent className="max-w-md">
          <Select value={locationId} onValueChange={setLocationId}>
            <SelectTrigger>
              <SelectValue
                placeholder={
                  locations.isLoading ? 'Loading locations…' : 'Select a location'
                }
              />
            </SelectTrigger>
            <SelectContent>
              {filteredLocations.map((l) => (
                <SelectItem key={l.locationId} value={String(l.locationId)}>
                  {l.locationName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      <DataTable<AllUserRow, unknown>
        tableId="bulk:add-location:v1"
        columns={columns}
        data={users.data ?? []}
        isLoading={users.isLoading}
        searchPlaceholder="Search users…"
        emptyTitle="No users available"
        emptyDescription="There are no users to assign."
        getRowId={(row) => String(row.userId)}
        enableRowSelection
        exportFilename="users.csv"
        pageSize={25}
        renderSelectionActions={(ids, clear) => (
          <Button
            size="sm"
            onClick={() => {
              if (!locationId) {
                toast.error('Pick a target location first.');
                return;
              }
              setPending({ ids, clear });
            }}
            disabled={!locationId || ids.length === 0 || bulkAdd.isPending}
          >
            <MapPin className="h-4 w-4" />
            Add {ids.length} to {locationLabel || 'location'}
          </Button>
        )}
      />

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(o) => !o && setPending(null)}
        title="Are you sure?"
        description={
          <>
            Add <span className="font-medium text-foreground">{pending?.ids.length ?? 0}</span> user
            {pending?.ids.length === 1 ? '' : 's'} to{' '}
            <span className="font-medium text-foreground">{locationLabel}</span>?
          </>
        }
        confirmLabel="Add users"
        loading={bulkAdd.isPending}
        onConfirm={performBulkAdd}
      />
    </FillPage>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (err && typeof err === 'object') {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
